/** 표시 모델의 손끝 중점. Dataset의 실제 TCP를 확인하는 근거가 아니다. */
import {
  SIGMA_HAND_DRIVER_LIMITS,
  SIGMA_HAND_FIXED_JOINTS,
  SIGMA_HAND_REVOLUTE_JOINTS,
  SIGMA_HAND_ROOT_LINK,
  type SigmaHandJointSpec,
} from "@/assets/models/sigmaHandKinematics.generated";
import {
  eulerMatrix,
  IDENTITY_MATRIX,
  multiplyMatrices,
  type Matrix3,
  type Vec3,
} from "./rotationMath";

export function rotateVector(matrix: Matrix3, value: Vec3): Vec3 {
  const dot = (row: Vec3) => row[0] * value[0] + row[1] * value[1] + row[2] * value[2];
  return [dot(matrix[0]), dot(matrix[1]), dot(matrix[2])];
}

export function addVectors(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

/** 손가락 벌림 0은 닫힘, 1은 열림이다. 그리퍼 출력(0 = 열림)이 아니라 1 − 출력이다. */
export function handDriverAngle(opening: number): number {
  const { upper, lower } = SIGMA_HAND_DRIVER_LIMITS;
  return upper + Math.min(1, Math.max(0, opening)) * (lower - upper);
}

const JOINTS = new Map(
  [...SIGMA_HAND_REVOLUTE_JOINTS, ...SIGMA_HAND_FIXED_JOINTS].map((joint) => [
    joint.childLink,
    joint,
  ]),
);

function spin([x, y, z]: Vec3, angle: number): Matrix3 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const v = 1 - c;
  return [
    [x * x * v + c, x * y * v - z * s, x * z * v + y * s],
    [y * x * v + z * s, y * y * v + c, y * z * v - x * s],
    [z * x * v - y * s, z * y * v + x * s, z * z * v + c],
  ];
}

/** 관절 표의 링크 원점을 손 베이스 프레임에서 계산한다(m). */
export function handLinkPosition(link: string, opening: number): Vec3 {
  const chain: SigmaHandJointSpec[] = [];
  for (let current = link; current !== SIGMA_HAND_ROOT_LINK;) {
    const joint = JOINTS.get(current);
    if (joint === undefined || chain.includes(joint))
      throw new Error("The hand joint chain is invalid");
    chain.unshift(joint);
    current = joint.parentLink;
  }
  let position: Vec3 = [0, 0, 0];
  let rotation = IDENTITY_MATRIX;
  for (const joint of chain) {
    position = addVectors(position, rotateVector(rotation, joint.originXyz));
    rotation = multiplyMatrices(rotation, eulerMatrix(joint.originRpy, "XYZ", false));
    if (joint.multiplier !== 0) {
      rotation = multiplyMatrices(
        rotation,
        spin(joint.axis, handDriverAngle(opening) * joint.multiplier),
      );
    }
  }
  return position;
}

export function gripCenterInBase(opening: number): Vec3 {
  const tips = SIGMA_HAND_FIXED_JOINTS.map((joint) => handLinkPosition(joint.childLink, opening));
  const sum = tips.reduce(addVectors, [0, 0, 0]);
  return [sum[0] / tips.length, sum[1] / tips.length, sum[2] / tips.length];
}

export const CLOSED_TCP_IN_BASE = gripCenterInBase(0);

/** 닫힘 TCP에서 현재 모델 파지 중심까지의 이동(m · 손 축). */
export function gripFromClosed(opening: number): Vec3 {
  const current = gripCenterInBase(opening);
  return [
    current[0] - CLOSED_TCP_IN_BASE[0],
    current[1] - CLOSED_TCP_IN_BASE[1],
    current[2] - CLOSED_TCP_IN_BASE[2],
  ];
}
